import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, ShieldCheck, ExternalLink } from "lucide-react";

const HUB_BASE = process.env.REACT_APP_HUB_BASE_URL || "https://controlhub.blackscott.co.za";
const HUB_LOGIN_RETURN = "/login?return_to=";

function formatErr(detail) {
  if (!detail) return "Sign-in failed.";
  if (typeof detail === "string") return detail;
  if (detail.message) return detail.message;
  return JSON.stringify(detail);
}

export default function SsoAcceptPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const ranRef = useRef(false);

  const [state, setState] = useState({ kind: "loading", message: "Signing you in via BlackScott Hub…" });

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const token = params.get("sso_token");
    // Strip the token from the URL IMMEDIATELY so it doesn't end up in
    // history, bookmarks, or referrer headers.
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", "/sso/accept");
    }

    if (!token) {
      setState({ kind: "missing" });
      // No cleanup — let the timer fire even under StrictMode double-mount.
      setTimeout(() => navigate("/login", { replace: true }), 1200);
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/sso/exchange", { token });
        if (data?.token) localStorage.setItem("token", data.token);
        if (data?.email) localStorage.setItem("user", JSON.stringify(data));
        setUser(data);
        navigate("/", { replace: true });
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        if (status === 409) {
          setState({ kind: "conflict", email: detail?.email, message: formatErr(detail) });
          return;
        }
        if (status === 401 && typeof detail === "string" && detail.toLowerCase().includes("expired")) {
          setState({ kind: "expired" });
          return;
        }
        setState({ kind: "bad-token", message: formatErr(detail) || e.message });
        const t = setTimeout(() => navigate("/login", { replace: true }), 2500);
        return () => clearTimeout(t);
      }
    })();
  }, [params, navigate, setUser]);

  const hubReturnUrl = `${HUB_BASE}${HUB_LOGIN_RETURN}${encodeURIComponent(window.location.origin)}`;

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="sso-accept-card">
        <img src="/cargodash-logo.svg" alt="CargoDash" className="h-9 mx-auto mb-4" />

        {state.kind === "loading" && (
          <div data-testid="sso-loading">
            <div className="mx-auto mb-4 w-10 h-10 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
            <p className="text-slate-600 text-sm">{state.message}</p>
          </div>
        )}

        {state.kind === "missing" && (
          <div data-testid="sso-missing">
            <AlertTriangle size={32} className="mx-auto text-amber-500 mb-3" />
            <p className="font-medium text-slate-800">No SSO token provided</p>
            <p className="text-slate-500 text-sm mt-2">Redirecting you to the login page…</p>
          </div>
        )}

        {state.kind === "expired" && (
          <div data-testid="sso-expired">
            <AlertTriangle size={32} className="mx-auto text-amber-500 mb-3" />
            <p className="font-semibold text-slate-800">Session expired</p>
            <p className="text-slate-500 text-sm mt-2 mb-5">
              Your Hub session has expired. Please return to BlackScott Hub to re-launch this app.
            </p>
            <a
              href={hubReturnUrl}
              data-testid="sso-return-to-hub"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-full"
            >
              <ExternalLink size={14} /> Return to Hub
            </a>
          </div>
        )}

        {state.kind === "bad-token" && (
          <div data-testid="sso-bad-token">
            <AlertTriangle size={32} className="mx-auto text-red-500 mb-3" />
            <p className="font-semibold text-slate-800">Sign-in failed</p>
            <p className="text-slate-500 text-sm mt-2">{state.message}</p>
            <p className="text-slate-400 text-xs mt-4">Redirecting to login…</p>
          </div>
        )}

        {state.kind === "conflict" && (
          <div data-testid="sso-conflict" className="text-left">
            <div className="flex items-center justify-center mb-4">
              <ShieldCheck size={32} className="text-amber-500" />
            </div>
            <p className="font-semibold text-slate-800 text-center">Account already exists</p>
            <p className="text-slate-600 text-sm mt-3">
              An account with the email <strong>{state.email}</strong> already exists
              as a <em>local</em> Skyworth login. We can't automatically link these two
              accounts — an admin must reconcile them in the Skyworth admin panel before
              you can sign in via BlackScott Hub.
            </p>
            <p className="text-slate-400 text-xs mt-4">
              Please ask your admin to deactivate the local account, or sign in directly
              with your existing email and password.
            </p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="mt-5 w-full bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold py-2 rounded-full"
              data-testid="sso-conflict-go-login"
            >
              Go to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
